// react
import { useContext, useState } from 'react';

// router
import { useParams } from 'react-router-dom';

// context
import { ContextBanner } from '../../../App.context';
import { ContextUsersPermissions } from '../Project/Project.context';

// hooks
import { useVerificationGroup } from '../../../hooks/useVerificationGroups';

// types
import { VerificationGroupMember } from '../../../@types';

// mui
import {
	alpha,
	Avatar,
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	MenuItem,
	Paper,
	Select,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';

// icons
import {
	Group as IconGroup,
	PersonAdd as IconAddMember,
	PersonRemove as IconRemoveMember,
	Star as IconLead,
} from '@mui/icons-material';

// design system
import { radius } from '../../../utils/designSystem';

// v2 components
import { HeaderDetailPage } from '../../ui/HeaderDetailPage/HeaderDetailPage';

// Component

export const PageVerificationGroup = () => {
	const theme = useTheme();
	const { groupId } = useParams<{ groupId: string }>();
	const { setBanner } = useContext(ContextBanner);
	const { usersPermissions: projectUsers } = useContext(ContextUsersPermissions);

	const {
		data: group,
		error,
		loading,
		update,
	} = useVerificationGroup({
		id: groupId ? parseInt(groupId) : undefined,
	});

	// State
	const [editDialogOpen, setEditDialogOpen] = useState(false);
	const [groupName, setGroupName] = useState('');
	const [groupDescription, setGroupDescription] = useState('');
	const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
	const [selectedUserId, setSelectedUserId] = useState<number | string>('');

	// Handlers
	const handleOpenEdit = () => {
		if (!group) return;
		setGroupName(group.name);
		setGroupDescription(group.description || '');
		setEditDialogOpen(true);
	};

	const handleSaveEdit = async () => {
		if (!group || !groupName.trim()) return;
		const res = await update({
			description: groupDescription || null,
			name: groupName.trim(),
		});
		if (!res.error) {
			setEditDialogOpen(false);
			setBanner({ message: 'Group updated.', severity: 'success' });
		} else {
			setBanner({ message: res.error.message, severity: 'error' });
		}
	};

	const handleAddMember = async () => {
		if (!selectedUserId) return;
		const res = await update({
			addMembers: [{ userId: Number(selectedUserId) }],
		});
		if (!res.error) {
			setAddMemberDialogOpen(false);
			setSelectedUserId('');
		} else {
			setBanner({ message: res.error.message, severity: 'error' });
		}
	};

	const handleRemoveMember = async (userId: number) => {
		const res = await update({ removeMembers: [userId] });
		if (res.error) {
			setBanner({ message: res.error.message, severity: 'error' });
		}
	};

	const handleToggleRole = async (member: VerificationGroupMember) => {
		const newRole = member.role === 'LEAD' ? 'MEMBER' : 'LEAD';
		const res = await update({
			addMembers: [{ role: newRole, userId: member.userId }],
		});
		if (res.error) {
			setBanner({ message: res.error.message, severity: 'error' });
		}
	};

	const members = group?.members || [];
	const leadCount = members.filter((m) => m.role === 'LEAD').length;

	return (
		<Box>
			{/* Header */}
			<HeaderDetailPage
				icon={IconGroup}
				breadcrumb={{ label: 'Verification Groups', to: '../verification-groups' }}
				title={group?.name || ''}
				variant="condition"
				loading={loading}
				onEdit={handleOpenEdit}
				editLabel="Edit Group"
				stats={[
					{ label: 'Members', value: members.length },
					{ label: 'Leads', value: leadCount },
					{
						label: 'Created',
						value: group?.created ? new Date(group.created).toLocaleDateString() : '—',
					},
				]}
			/>

			{/* Error */}
			{error && (
				<Paper
					sx={{
						p: 1.5,
						mx: 3,
						mt: 2,
						bgcolor: alpha(theme.palette.error.main, 0.06),
						border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
						borderRadius: radius.md,
					}}>
					<Typography variant="body2" color="error">
						{error}
					</Typography>
				</Paper>
			)}

			{/* Content */}
			<Box sx={{ px: 3, pt: 2, pb: 3 }}>
				{/* Description */}
				{group?.description && (
					<Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
						{group.description}
					</Typography>
				)}

				{/* Members section */}
				<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
					<Typography variant="h6" fontWeight={600}>
						Members
					</Typography>
					<Button
						variant="outlined"
						startIcon={<IconAddMember />}
						onClick={() => {
							setSelectedUserId('');
							setAddMemberDialogOpen(true);
						}}
						sx={{ borderRadius: radius.md, textTransform: 'none', fontWeight: 600 }}>
						Add Member
					</Button>
				</Stack>

				{members.length === 0 && !loading && (
					<Paper
						sx={{
							p: 3,
							textAlign: 'center',
							borderRadius: radius.md,
							border: `1px dashed ${theme.palette.divider}`,
						}}>
						<Typography variant="body2" color="text.secondary">
							No members yet. Add project users to this verification group.
						</Typography>
					</Paper>
				)}

				{members.length > 0 && (
					<TableContainer component={Paper} variant="outlined" sx={{ borderRadius: radius.md }}>
						<Table>
							<TableHead>
								<TableRow sx={{ bgcolor: alpha(theme.palette.grey[500], 0.06) }}>
									<TableCell sx={{ fontWeight: 600 }}>User</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>Added</TableCell>
									<TableCell sx={{ fontWeight: 600, width: 80 }} align="right">
										Actions
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{members.map((member) => (
									<TableRow key={member.id}>
										<TableCell>
											<Stack direction="row" spacing={1.5} alignItems="center">
												<Avatar
													sx={{
														width: 28,
														height: 28,
														fontSize: 12,
														bgcolor: alpha(theme.palette.primary.main, 0.1),
														color: 'primary.main',
													}}>
													{(member.userName || member.userEmail || '?').charAt(0).toUpperCase()}
												</Avatar>
												<Box>
													<Typography variant="body2" fontWeight={500}>
														{member.userName || member.userEmail || `User #${member.userId}`}
													</Typography>
													{member.userEmail && member.userName && (
														<Typography variant="caption" color="text.secondary">
															{member.userEmail}
														</Typography>
													)}
												</Box>
											</Stack>
										</TableCell>
										<TableCell>
											<Chip
												icon={
													member.role === 'LEAD' ? <IconLead sx={{ fontSize: 14 }} /> : undefined
												}
												label={member.role}
												size="small"
												color={member.role === 'LEAD' ? 'primary' : 'default'}
												variant="outlined"
												onClick={() => handleToggleRole(member)}
												sx={{ fontSize: 12, cursor: 'pointer' }}
											/>
										</TableCell>
										<TableCell>
											<Typography variant="body2" color="text.secondary">
												{member.created ? new Date(member.created).toLocaleDateString() : '—'}
											</Typography>
										</TableCell>
										<TableCell align="right">
											<Tooltip title="Remove member">
												<IconButton size="small" onClick={() => handleRemoveMember(member.userId)}>
													<IconRemoveMember sx={{ fontSize: 16 }} />
												</IconButton>
											</Tooltip>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				)}
			</Box>

			{/* Edit Group Dialog */}
			<Dialog
				open={editDialogOpen}
				onClose={() => setEditDialogOpen(false)}
				maxWidth="sm"
				fullWidth
				PaperProps={{ sx: { borderRadius: radius.lg } }}>
				<DialogTitle sx={{ fontWeight: 700 }}>Edit Group</DialogTitle>
				<DialogContent>
					<Stack spacing={2} sx={{ pt: 1 }}>
						<TextField
							label="Group Name"
							value={groupName}
							onChange={(e) => setGroupName(e.target.value)}
							required
							fullWidth
							size="small"
							autoFocus
						/>
						<TextField
							label="Description (optional)"
							value={groupDescription}
							onChange={(e) => setGroupDescription(e.target.value)}
							fullWidth
							size="small"
							multiline
							rows={2}
						/>
					</Stack>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button
						onClick={() => setEditDialogOpen(false)}
						sx={{ borderRadius: radius.md, textTransform: 'none' }}>
						Cancel
					</Button>
					<Button
						variant="contained"
						onClick={handleSaveEdit}
						disabled={!groupName.trim()}
						sx={{ borderRadius: radius.md, textTransform: 'none', fontWeight: 600 }}>
						Save Changes
					</Button>
				</DialogActions>
			</Dialog>

			{/* Add Member Dialog */}
			<Dialog
				open={addMemberDialogOpen}
				onClose={() => setAddMemberDialogOpen(false)}
				maxWidth="xs"
				fullWidth
				PaperProps={{ sx: { borderRadius: radius.lg } }}>
				<DialogTitle sx={{ fontWeight: 700 }}>Add Member</DialogTitle>
				<DialogContent>
					<Stack spacing={2} sx={{ pt: 1 }}>
						<Select
							value={selectedUserId}
							onChange={(e) => setSelectedUserId(e.target.value)}
							displayEmpty
							fullWidth
							size="small">
							<MenuItem value="" disabled>
								Select a project member...
							</MenuItem>
							{projectUsers
								.filter((u: { user_id?: number; id?: number; name?: string; email?: string }) => {
									return !members.some((m) => m.userId === (u.user_id || u.id));
								})
								.map((u: { user_id?: number; id?: number; name?: string; email?: string }) => (
									<MenuItem key={u.user_id || u.id} value={u.user_id || u.id}>
										{u.name || u.email || `User #${u.user_id || u.id}`}
									</MenuItem>
								))}
						</Select>
					</Stack>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button
						onClick={() => setAddMemberDialogOpen(false)}
						sx={{ borderRadius: radius.md, textTransform: 'none' }}>
						Cancel
					</Button>
					<Button
						variant="contained"
						onClick={handleAddMember}
						disabled={!selectedUserId}
						sx={{ borderRadius: radius.md, textTransform: 'none', fontWeight: 600 }}>
						Add Member
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};
